<template>
<modal :visible.sync="display" :width="'fit-content'">
  <template slot="header">
    <h3>{{ title }}</h3>
  </template>
  <el-form
    :model="form"
    :rules="rules"
    @submit.native.prevent="submit"
    ref="form"
  >
    <el-row :gutter="50">
      <el-col :xs="24" :sm="12">
        <el-form-item
          :label="$t('profile.newPassword.email.text')">
          <el-input
            v-model="email"
            :placeholder="$t('profile.newPassword.email.text')"
            disabled
            suffix-icon="el-icon-message">
          </el-input>
        </el-form-item>
        <el-form-item
          v-if="profile && profile.created_password"
          :label="$t('profile.newPassword.oldPassword.text')"
          prop="oldPassword">
          <el-input
            v-model="form.oldPassword"
            :placeholder="$t('profile.newPassword.oldPassword.text')"
            autofocus
            show-password
            suffix-icon="el-icon-lock">
          </el-input>
        </el-form-item>
        <el-form-item :label="$t('profile.newPassword.newPassword.text')" prop="newPassword">
          <el-input
            v-model="form.newPassword"
            :placeholder="$t('profile.newPassword.newPassword.text')"
            show-password
            suffix-icon="el-icon-lock">
          </el-input>
        </el-form-item>
        <el-form-item
          :label="$t('profile.newPassword.newPasswordConfirmation.text')"
          prop="newPasswordConfirmation">
          <el-input
            v-model="form.newPasswordConfirmation"
            :placeholder="$t('profile.newPassword.newPasswordConfirmation.text')"
            show-password
            suffix-icon="el-icon-lock">
          </el-input>
        </el-form-item>
      </el-col>
      <el-col :xs="24" :sm="12">
        <p>{{ $t('profile.newPassword.newPassword.rules.title') }}</p>
        <ul v-if="profile && profile.is_minarm">
          <li>{{ $t('profile.newPassword.newPassword.rules.lengthCondition') }}</li>
          <li>{{ $t('profile.newPassword.newPassword.rules.caseCondition') }}</li>
          <li>{{ $t('profile.newPassword.newPassword.rules.specialCondition') }}</li>
        </ul>
        <ul v-else-if="profile && !profile.is_minarm">
          <li>{{ $t('profile.newPassword.newPassword.rules.lengthCondition2') }}</li>
        </ul>
      </el-col>
    </el-row>

    <el-button
      v-loading="isPostPasswordLoading"
      :disabled="isPostPasswordLoading"
      native-type="submit"
      type="primary">
      {{ submitText }}
    </el-button>
  </el-form>
</modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Modal from '@/components/utils/Modal.vue';

export default {
  name: 'SaveAndQuitModal',
  components: { Modal },
  data() {
    const self = this;
    const regexValidation = (rule, value, callback) => {
      if (!self.profile.is_minarm || rule.regex.test(value)) {
        return callback();
      }
      return callback(new Error());
    };
    const equalPasswordValidation = (rule, value, callback) => {
      if (self.form.newPassword !== value) {
        return callback(new Error());
      }
      return callback();
    };
    const lengthValidation = (rule, value, callback) => {
      if ((self.profile.is_minarm && value.length) > 9
        || (!self.profile.is_minarm && value.length) > 7) {
        return callback();
      }
      return callback(self.profile.is_minarm
        ? self.$t('profile.newPassword.newPassword.lengthCondition')
        : self.$t('profile.newPassword.newPassword.lengthCondition2'));
    };
    return {
      form: {
        newPassword: '',
        oldPassword: '',
        newPasswordConfirmation: '',
      },
      rules: {
        oldPassword: [
          {
            required: true,
            message: this.$t('profile.newPassword.oldPassword.warning'),
            trigger: 'blur',
          },
        ],
        newPassword: [
          {
            required: true,
            message: this.$t('profile.newPassword.newPassword.warning'),
            trigger: 'blur',
          },
          {
            validator: lengthValidation,
            trigger: 'blur',
          },
          {
            validator: regexValidation,
            regex: /^(?=.*[a-z])(?=.*[A-Z]).+$/,
            message: this.$t('profile.newPassword.newPassword.caseCondition'),
            trigger: 'blur',
          },
          {
            validator: regexValidation,
            regex: /[ !@#$%^&*()_+\-=[\]{};':"|,.<>/?]/,
            message: this.$t('profile.newPassword.newPassword.specialCondition'),
            trigger: 'blur',
          },
        ],
        newPasswordConfirmation: [
          {
            required: true,
            message: this.$t('profile.newPassword.newPasswordConfirmation.warning'),
            trigger: 'blur',
          },
          {
            validator: equalPasswordValidation,
            message: this.$t('profile.newPassword.newPasswordConfirmation.passwordConfirmationEqual'),
            trigger: 'blur',
          },
        ],
      },
    };
  },
  props: {
    visible: { type: Boolean, default: false },
  },
  computed: {
    ...mapGetters({
      profile: 'authStore/profile',
      isPostPasswordLoading: 'authStore/isPostPasswordLoading',
    }),
    display: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
      },
    },
    title() {
      if (this.profile) {
        return this.profile.created_password
          ? this.$t('profile.newPassword.title2')
          : this.$t('profile.newPassword.title1');
      }
      return '';
    },
    submitText() {
      if (this.profile) {
        return this.profile.created_password
          ? this.$t('profile.newPassword.submit.change')
          : this.$t('profile.newPassword.submit.new');
      }
      return '';
    },
    email() {
      return this.profile ? this.profile.email : '';
    },
  },
  methods: {
    ...mapActions({
      getProfile: 'authStore/getProfile',
      postPassword: 'authStore/postPassword',
    }),
    submit() {
      this.$refs.form.validate((valid) => {
        if (valid) {
          this.postPassword({
            old_password: this.form.oldPassword,
            new_password: this.form.newPassword,
          }).then(() => {
            this.form = {
              oldPassword: '',
              newPassword: '',
              newPasswordConfirmation: '',
            };
          });
        }
      });
    },
  },
};
</script>
<style lang="scss">
.scroll{
  overflow-x: hidden;
  overflow-y: auto;
}
.only-mobile{
  margin-left: auto;
  margin-right: auto;
  position: absolute;
  bottom: 60px;
  left: 35%;
  display: flex;
  @include breakpoint(tablet){
    display: none;
  }
}
.not-mobile{
  display: none;
  @include breakpoint(tablet){
    display: unset;
  }
}
</style>
